// src/App.js
import React, { useState, useEffect, useRef } from 'react';
import Dashboard from './pages/Dashboard';
import TemplateBuilder from './pages/TemplateBuilder';
import Templates from './pages/Templates';
import Inspections from './pages/Inspections';
import Projects from './pages/Projects';
import ArchivedProjects from './pages/ArchivedProjects';
import Team from './pages/Team';
import Gear from './pages/Gear';
import Profile from './pages/Profile';
import AdminPanel from './pages/AdminPanel';
import Login from './pages/Login';
import GearDetail from './pages/GearDetail';
import GearAdd from "./pages/GearAdd";
import TransferEquipmentModal from "./components/TransferEquipmentModal";
import AddProjectModal from "./components/AddProjectModal";
import InviteModal from "./components/InviteModal";
import OrganizationSettings from './pages/OrganizationSettings';
import Sidebar from './Sidebar';
import TopBar from "./components/TopBar";
import Register from "./pages/Register";
// import NewInspection from "./pages/NewInspection"; // removed
import FillInspection from "./pages/FillInspection";
import ProjectDetail from "./pages/ProjectDetail";
import './store/projectStore';
import { Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { api } from './lib/api';
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "./firebase";
import { useProjectStore } from "./store/projectStore";
import { useGearStore } from "./store/gearStore";

import './styles/App.css';

export default function App() {
  const [user, setUser] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [showMoreMobile, setShowMoreMobile] = useState(false);
  const [showPlusMenu, setShowPlusMenu] = useState(false);
  const [selectedGear, setSelectedGear] = useState(null);

  // Desktop Top Bar injection (page-specific content)
  const [topbarLeft, setTopbarLeft] = useState(null);
  const [topbarCenter, setTopbarCenter] = useState(null);

  // Let TopBar open the mobile nav drawer inside Sidebar
  const openMobileNavRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const profileUid = params.get('uid');

  // ✅ Force auth-gated navigation on app.moraviaprime.ca
  useEffect(() => {
    const isPublic =
      location.pathname === "/login" ||
      location.pathname === "/register";

    if (!user && !isPublic) {
      navigate("/login", { replace: true });
      return;
    }

    if (user && location.pathname === "/login") {
      navigate("/dashboard", { replace: true });
    }
  }, [user, location.pathname, navigate]);

  // Logout (Firebase auth)
  const logout = async () => {
    try {
      await signOut(auth);
    } catch (e) {
      // ignore
    }
    setUser(false);
    setUserProfile(null);
  };

  // 🟢 All modal states
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showTransferEquipmentModal, setShowTransferEquipmentModal] = useState(false);

  const [showArchivedProjects, setShowArchivedProjects] = useState(false);

  const plusBtnRef = useRef(null);
  const plusMenuRef = useRef(null);
  const moreBtnRef = useRef(null);
  const moreDrawerRef = useRef(null);
  const { projects, fetchProjects } = useProjectStore();
  const { gear, setGear } = useGearStore();

  // Use real gear list for modals
  const gearList = Array.isArray(gear) ? gear : [];
  const team = [];
  const projectList = Array.isArray(projects) ? projects : [];

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    if (!projects || projects.length === 0) {
      Promise.resolve(fetchProjects?.()).catch(() => {});
    }
  }, [projects, fetchProjects]);

  // Load gear from backend so it persists across refresh
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const res = await api.get('/gear');
        if (!mounted) return;

        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.gear)
          ? res.gear
          : Array.isArray(res?.data)
          ? res.data
          : [];

        setGear(list);
      } catch (e) {
        console.warn('Failed to load /gear:', e);
      }
    })();

    return () => { mounted = false; };
  }, [setGear]);

  // Load current user from Firebase Auth + Firestore profile
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      setLoadingProfile(true);

      if (!fbUser) {
        setUser(false);
        setUserProfile(null);
        setLoadingProfile(false);
        return;
      }

      setUser(true);

      try {
        const snap = await getDoc(doc(db, "users", fbUser.uid));
        const data = snap.exists() ? snap.data() : null;

        setUserProfile({
          uid: fbUser.uid,
          email: fbUser.email,
          name: data?.name || fbUser.email,
          role: (data?.role || "User").toLowerCase(), // keep your existing checks working
          team: data?.team || "",
          lastSeen: data?.lastSeen || null,
          organizationLogo: data?.organizationLogo || null,
        });
      } catch (e) {
        // If Firestore profile missing, still allow app access with basic profile
        setUserProfile({
          uid: fbUser.uid,
          email: fbUser.email,
          name: fbUser.email,
          role: "user",
          team: "",
          lastSeen: null,
        });
      } finally {
        setLoadingProfile(false);
      }
    });

    return () => unsub();
  }, []);

  // Sync currentPage to URL on initial load
  useEffect(() => {
    if (location.pathname.startsWith('/profile')) {
      setCurrentPage('profile');
    } else if (location.pathname.startsWith('/team')) {
      setCurrentPage('team');
    } else if (location.pathname.startsWith('/dashboard')) {
      setCurrentPage('dashboard');
    } else if (location.pathname.startsWith('/templates')) {
      setCurrentPage('templates');
    } else if (location.pathname === '/template-builder') {
      setCurrentPage('templateBuilder');
    } else if (location.pathname.startsWith('/inspections')) {
      setCurrentPage('inspections');
    } else if (location.pathname.startsWith('/projects/')) {
      setCurrentPage('projectDetail');
    } else if (location.pathname.startsWith('/projects')) {
      setCurrentPage('projects');
    } else if (location.pathname.startsWith('/gearDetail')) {
      setCurrentPage('gearDetail');
    } else if (location.pathname.startsWith('/gear/add')) {
      setCurrentPage('gearAdd');
    } else if (location.pathname.startsWith('/gear')) {
      setCurrentPage('gear');
    } else if (location.pathname.startsWith('/organizationSettings')) {
      setCurrentPage('organizationSettings');
    } else if (location.pathname.startsWith('/adminPanel')) {
      setCurrentPage('adminPanel');
    } else {
      setCurrentPage('dashboard'); // Default fallback
    }
  }, [location.pathname, location.search]);

  // Clear any injected TopBar content when navigating between pages
  useEffect(() => {
    setTopbarLeft(null);
    setTopbarCenter(null);
  }, [location.pathname]);

  // Click-outside to close mobile menus
  useEffect(() => {
    const handler = e => {
      if (
        showPlusMenu &&
        plusMenuRef.current &&
        !plusMenuRef.current.contains(e.target) &&
        plusBtnRef.current &&
        !plusBtnRef.current.contains(e.target)
      ) {
        setShowPlusMenu(false);
      }
      if (
        showMoreMobile &&
        moreDrawerRef.current &&
        !moreDrawerRef.current.contains(e.target) &&
        moreBtnRef.current &&
        !moreBtnRef.current.contains(e.target)
      ) {
        setShowMoreMobile(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [showPlusMenu, showMoreMobile]);

  // Mobile "+" quick actions that should open modals
  useEffect(() => {
    if (currentPage === 'transferEquipment') {
      setShowTransferEquipmentModal(true);
      setCurrentPage('gear');
      return;
    }
    if (currentPage === 'inviteMember') {
      setShowInviteModal(true);
      setCurrentPage('team');
      return;
    }
  }, [currentPage]);

  // Main content rendering (move this above the return!)
  const { role } = userProfile || {};
  const isAdmin = role === 'admin';
  const canBuilder = isAdmin || role === 'manager';

  const renderContent = () => {
    if (currentPage === 'templateBuilder' && !canBuilder) {
      return <div className="p-6 text-red-500">Access denied.</div>;
    }
    if (currentPage === 'adminPanel' && !isAdmin) {
      return <div className="p-6 text-red-500">Admins only.</div>;
    }
    switch (currentPage) {
      case 'dashboard':
        return (
          <Dashboard
            setCurrentPage={setCurrentPage}
            openAddProject={() => setShowAddProjectModal(true)}
            openInviteMember={() => setShowInviteModal(true)}
            openAddGear={() => {
              setCurrentPage("gearAdd");
              navigate("/gear/add");
            }}
            openTransferEquipment={() => setShowTransferEquipmentModal(true)}
          />
        );
      case 'templates':
        return <Templates />;
      case 'templateBuilder':
        return <TemplateBuilder />;
      case 'inspections':
        return <Inspections />;
      case 'projects':
        return showArchivedProjects
          ? <ArchivedProjects onBack={() => setShowArchivedProjects(false)} />
          : <Projects onShowArchived={() => setShowArchivedProjects(true)} />;
      case 'projectDetail':
        return <ProjectDetail />;
      case 'team':
        return <Team setCurrentPage={setCurrentPage} />;
      case 'gear':
        return (
          <Gear
            setSelectedGear={setSelectedGear}
            setCurrentPage={setCurrentPage}
            setTopbarLeft={setTopbarLeft}
          />
        );
      case 'gearAdd':
        return <GearAdd setCurrentPage={setCurrentPage} />;
      case 'profile':
        return <Profile setCurrentPage={setCurrentPage} uid={profileUid} />;
      case 'adminPanel':
        return <AdminPanel />;
      case 'gearDetail':
        return (
          <GearDetail
            gear={selectedGear}
            setCurrentPage={setCurrentPage}
            setTopbarCenter={setTopbarCenter}
          />
        );
      case 'organizationSettings':
        return <OrganizationSettings />;
      default:
        return <Dashboard />;
    }
  };

  // 🟢 This is the main change: hide sidebar/layout for /template-builder!
  const isTemplateBuilder = location.pathname === "/template-builder";

  return (
    <Routes>
      {/* Invite registration route */}
      <Route path="/register" element={<Register />} />

      {/* ✅ Public login route */}
      <Route
        path="/login"
        element={
          <Login
            onLogin={(page) => {
              setUser(true);
              setCurrentPage(page || "dashboard");
            }}
          />
        }
      />
      {/* NEW INSPECTION ROUTE (legacy path → FillInspection) */}
      <Route path="/inspections/new/:templateId" element={<FillInspection />} />
      {/* TEMPLATE BUILDER FULLSCREEN ROUTE */}
      <Route path="/template-builder/:templateId" element={<TemplateBuilder />} />
      {/* 🟢 REDIRECT BUILDER WITH NO ID */}
      <Route path="/template-builder" element={<TemplateBuilder />} />
      {/* FILL INSPECTION ROUTE */}
      <Route path="/inspections/fill/:templateId" element={<FillInspection />} />
      {/* ✅ PROJECT DETAIL ROUTE WITH LAYOUT */}
      <Route
        path="/projects/:id"
        element={
          !user ? (
            <Navigate to="/login" replace />
          ) : loadingProfile || !userProfile ? (
            <div className="p-6">Loading...</div>
          ) : (
            <div className="app-container">
              <div className="main-layout">
                <Sidebar
                  userProfile={userProfile}
                  setCurrentPage={setCurrentPage}
                  currentPage={currentPage}
                  signOut={logout}
                  organizationLogo={userProfile?.organizationLogo}
                  openMobileNavRef={openMobileNavRef}
                />

                <TopBar
                  organizationLogo={userProfile?.organizationLogo}
                  topbarLeft={topbarLeft}
                  topbarCenter={topbarCenter}
                  onOpenMobileNav={() => openMobileNavRef.current?.()}
                />

                <div className="content-container">
                  {/* Desktop spacer so fixed top bar doesn't overlap content */}
                  <div className="hidden md:block" style={{ height: 44 }} />

                  {/* Notification bell moved to Sidebar top bar (mobile + desktop) */}
                  <ProjectDetail />
                </div>
              </div>
            </div>
          )
        }
      />

      {/* All other app routes */}
      <Route
        path="*"
        element={
          !user ? (
            <Navigate to="/login" replace />
          ) : loadingProfile || !userProfile ? (
            <div className="p-6">Loading...</div>
          ) : isTemplateBuilder ? (
            // ⬇️ Fullscreen builder - no sidebar or extra containers
            <TemplateBuilder />
          ) : (
            <div className="app-container">
              <div className="main-layout">
                <Sidebar
                  userProfile={userProfile}
                  setCurrentPage={setCurrentPage}
                  currentPage={currentPage}
                  signOut={logout}
                  organizationLogo={userProfile?.organizationLogo}
                  openMobileNavRef={openMobileNavRef}
                />

                <TopBar
                  organizationLogo={userProfile?.organizationLogo}
                  topbarLeft={topbarLeft}
                  topbarCenter={topbarCenter}
                  onOpenMobileNav={() => openMobileNavRef.current?.()}
                />

                <div className="content-container">
                  {/* Desktop spacer so fixed top bar doesn't overlap content */}
                  <div className="hidden md:block" style={{ height: 36 }} />

                  {/* Notification bell moved to Sidebar top bar (mobile + desktop) */}
                  {renderContent()}
                </div>
              </div>

              {/* Modals at the root level */}
              {showAddProjectModal && (
                <AddProjectModal
                  onClose={() => setShowAddProjectModal(false)}
                  onAdd={(project) => {
                    alert(`New project: ${project.name}`);
                    setShowAddProjectModal(false);
                  }}
                />
              )}
              {showInviteModal && (
                <InviteModal
                  onClose={() => setShowInviteModal(false)}
                  onInvite={(email) => {
                    alert(`Invitation sent to: ${email}`);
                    setShowInviteModal(false);
                  }}
                />
              )}
              {showTransferEquipmentModal && (
                <TransferEquipmentModal
                  gearList={gearList}
                  team={team}
                  projects={projectList}
                  onClose={() => setShowTransferEquipmentModal(false)}
                />
              )}
            </div>
          )
        }
      />
    </Routes>
  );
}
